import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ToppingItemOptimized, { OnToppingItemChangeProps } from '../../Toppings/ToppingItem/ToppingItemOptimized/ToppingItemOptimized';
import { selectors } from '../../../slice/pizza.slice';
import {
  PizzaIngredient,
  PizzaIngredientOption
} from '@/builders/pizza/dataTransformers/builderTypes';
import { onPizzaToppingClick } from '@/dataAnalytics/dataAnalyticsHelper';
import { ToppingsSelectionAnalytics } from '@/dataAnalytics/analyticsTypes';
import Portion from '@/common/Portion';
import { toIngredientWithPortionAndPlacement } from '../../../dataTransformers/toIngredient';
import useAnalytics from '@/dataAnalytics/hooks/useAnalytics';
import { useLineup } from '@/builders/deals/hooks/useLineup';
import { openModal } from '@/localization/actions';

const useStyles = makeStyles(() => ({
  root: {
    marginTop: '12px'
  },
  title: {
    marginBottom: '12px',
    fontWeight: 600
  }
}));

interface FinisherPickerProps {
  finisherOptions: PizzaIngredientOption[];
  selectedFinishers: PizzaIngredient[];
  onFinishersUpdate: (finishers: PizzaIngredient[]) => void;
  maxAllowedFinishers: number;
  multiSelectEnabled?: boolean;
}

const FinisherPicker = ({
  finisherOptions,
  selectedFinishers,
  onFinishersUpdate,
  maxAllowedFinishers,
  multiSelectEnabled = true
}: FinisherPickerProps): JSX.Element => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const analytics = useAnalytics();
  const { isLineup } = useLineup();
  const pizzaName = useSelector(selectors.selectPizzaSummaryTitle);

  const onPreventToAdd = (maxAllowed: number) => {
    dispatch(
      openModal({
        title: 'Finisher limit reached!',
        body: `You can only select up to ${maxAllowed} finisher${maxAllowed > 1 ? 's' : ''}`,
        cta: {
          text: 'GOT IT'
        }
      })
    );
  };

  const findSelectedFinisher = (finisherId: string) => 
    selectedFinishers.find((finisher) => finisher.id === finisherId);

  const handleFinisherChange = (topping: PizzaIngredientOption, { isSelected }: OnToppingItemChangeProps) => {
    const action = isSelected ? ToppingsSelectionAnalytics.ADD : ToppingsSelectionAnalytics.UNCHECK;
    
    analytics.push(() => onPizzaToppingClick(pizzaName || '', topping.name, action, isLineup));
    
    if (multiSelectEnabled) {
      // Multi-select mode: handle arrays
      if (isSelected) {
        if (selectedFinishers.length >= maxAllowedFinishers) {
          onPreventToAdd(maxAllowedFinishers);
          return;
        }
        
        // Add the finisher to the selection
        const newFinisher = toIngredientWithPortionAndPlacement(topping, Portion.REGULAR, 'whole');
        onFinishersUpdate([...selectedFinishers, newFinisher]);
      } else {
        // Remove the finisher from the selection
        const updatedFinishers = selectedFinishers.filter((finisher) => finisher.id !== topping.id);
        onFinishersUpdate(updatedFinishers);
      }
    } else if (isSelected) {
      const newFinisher = toIngredientWithPortionAndPlacement(topping, Portion.REGULAR, 'whole');
      onFinishersUpdate([newFinisher]);
    } else {
      onFinishersUpdate([]);
    }
  };

  return (
    <Grid container direction="column" className={classes.root}>
      <Grid container direction="column" spacing={1}>
        {finisherOptions.map((finisher) => {
          const selectedIngredient = findSelectedFinisher(finisher.id);
          return (
            <Grid item key={finisher.id}>
              <ToppingItemOptimized
                topping={finisher}
                selectedIngredient={selectedIngredient}
                extraPrice={null}
                onChange={handleFinisherChange}
                reachedToppingLimit={false}
                itemType="finisher"
              />
            </Grid>
          );
        })}
      </Grid>
    </Grid>
  );
};

export default FinisherPicker;
